"use client";

import React, { useState, useEffect } from "react";
import { Alert, Box, Typography, Tabs, Tab } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import PhoneNumberForm from "@/app/components/modals/verify/phoneNumberForm";
import CodeConfirmForm from "@/app/components/modals/verify/codeConfirmForm";
import dynamic from "next/dynamic";
import { CreditCard } from "@mui/icons-material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";

const StripeCardForm = dynamic(() => import("./StripeCardForm.jsx"), {
  loading: () => (
    <Box sx={{ textAlign: "center", padding: "20px" }}>
      <Typography variant="body1" color="textSecondary">
        Loading payment form...
      </Typography>
    </Box>
  ),
  ssr: false,
});

const VerifyIdentityModal = ({
  open,
  handleClose,
  currentMeetingData = null,
  contextMessage = null,
}) => {
  const theme = useTheme();
  const profile = useSelector((state) => state.user.profile);

  const [activeTab, setActiveTab] = useState(0); // 0 = phone, 1 = card
  const [phoneSent, setPhoneSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreditCardLoading, setIsCreditCardLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setPhoneSent(false);
      setPhoneNumber("");
      setIsLoading(false);
      setIsCreditCardLoading(false);
      setActiveTab(0);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setErrorMessage("");
    setPhoneSent(false);
    setPhoneNumber("");
  };

  const handlePhoneSubmitted = (submittedPhoneNumber) => {
    setPhoneNumber(submittedPhoneNumber);
    setPhoneSent(true);
  };

  const getDescription = () => {
    if (activeTab === 0) {
      return phoneSent
        ? "Enter the 6-digit code sent to your phone to verify your number. The code will expire shortly."
        : "Enter your mobile number to get a one-time SMS code. We'll send a 6-digit code to verify your number.";
    }
    return "Enter your credit card to verify identity. You won’t be charged until you schedule a meeting.";
  };

  // Build the body content for the ConfirmDialog
  const bodyContent = (
    <Box>
      {contextMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {contextMessage}
        </Alert>
      )}
      {/* Keep the method choice above the form at every screen size. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          mb: 3,
          gap: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", fontSize: "0.9375rem" }}
        >
          Final step! Select your verification method.
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="Verification method"
          sx={{
            minHeight: "auto",
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
            "& .MuiTab-root": {
              minHeight: 40,
              fontSize: "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              minWidth: 0,
              padding: "8px 16px",
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIphoneIcon fontSize="small" />
                Phone
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCard fontSize="small" />
                Card
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Description text */}
      <Typography
        variant="body1"
        sx={{
          mb: 3,
          color: "text.secondary",
          fontSize: "0.9375rem",
          lineHeight: 1.65,
        }}
      >
        {getDescription()}
      </Typography>

      {/* Form Content */}
      <Box>
        {activeTab === 0 ? (
          phoneSent ? (
            <CodeConfirmForm
              stacked
              phoneNumber={phoneNumber}
              handleClose={handleClose}
              setErrorMessage={setErrorMessage}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              currentMeetingData={currentMeetingData}
            />
          ) : (
            <PhoneNumberForm
              stacked
              countryCode={profile?.country_code}
              onPhoneSubmitted={handlePhoneSubmitted}
              setErrorMessage={setErrorMessage}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          )
        ) : (
          <StripeCardForm
            stacked
            handleClose={handleClose}
            setErrorMessage={setErrorMessage}
            setIsCreditCardLoading={setIsCreditCardLoading}
            isCreditCardLoading={isCreditCardLoading}
            currentMeetingData={currentMeetingData}
          />
        )}

        <Box sx={{ mt: 2 }}>
          {errorMessage ? (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {activeTab === 0
                ? phoneSent
                  ? "Enter the 6-digit code sent to your phone."
                  : "Used only for verification, never shared."
                : "All transactions are secure and encrypted."}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      title="Verify it's you"
      body={bodyContent}
      showCloseIcon={true}
      showConfirmButton={false}
      showIgnoreButton={false}
      disableBackdropClick={true}
      disableEscapeKeyDown={false}
      modalStyle="center"
      backdropStyle="frosted"
      customStyle={{
        width: "min(620px, calc(100vw - 48px))",
        px: 0,
        borderRadius: "10px",
        maxHeight: "calc(100dvh - 48px)",
        overflowY: "auto",
      }}
      contentSx={{ px: 4, pt: 4, pb: 3 }}
      titleSx={{
        fontSize: "1.625rem",
        lineHeight: 1.25,
        letterSpacing: "-0.5px",
      }}
      mobileContentSx={{ borderRadius: "10px 10px 0 0" }}
    />
  );
};

export default VerifyIdentityModal;
